import { UpperCaseCountry } from '@models/CountriesAndLanguages';
import { DetailedTitle, Title } from '@models/Title';

export type Bookmark = {
  dateTime: Date;
  isWatched: boolean;
  masterId: string;
  position: number;
  providerIdentifier: string;
  seriesMasterId: string;
};

export type HuaweiBookmark = {
  bookmarkType: string;
  itemID: string;
  rangeTime: string;
  updateTime: string;
};

export type TEntitlementProduct = {
  contentType: 'svod' | 'tvod' | 'vod';
  masterId: string;
  name: string;
  platformTitleId: string;
  price: number;
  productId: string;
  purchaseDate: Date;
};

export enum PlatformId {
  None = '0',
  Huawei = '1',
  CVP = '2',
  All = '3',
  Default = '4',
}

export enum SourceId {
  HUAWEI_VOD = 'Huawei VOD',
  HUAWEI_EPG = 'Huawei EPG',
  COMCAST = 'Comcast',
  NRK = 'NRK',
  MAX = 'Max',
  TV2 = 'Tv2',
  VIAPLAY = 'Viaplay',
}

export type Subscriber = {
  name: string;
  profileId: string;
  subscriberId: string;
  vodPlatform?: PlatformId;
};

export type SubscriberWithToken = Subscriber & { atsToken: string };

export type DecodedAtsToken = {
  aud: string;
  exp: number;
  huaweiTicket: string;
  iss: string;
  nbf: number;
  partnerId: string;
  partnerName: string;
  subscriberCountry: UpperCaseCountry;
  subscriberId: string;
  vodPlatform: PlatformId;
};

export type EntitlementTitleType = Pick<
  Title,
  | 'descriptionLong'
  | 'episode'
  | 'flags'
  | 'genres'
  | 'masterId'
  | 'originalTitle'
  | 'productionYear'
  | 'season'
  | 'series'
  | 'slug'
  | 'titleName'
  | 'type'
> &
  Pick<DetailedTitle, 'descriptionLong' | 'duration' | 'episode'>;

export type ContinueWatchingBookmark = EntitlementTitleType & {
  isRepeatingSeries: boolean;
  isWatched: boolean;
  norwegianMediaAuthorityId: string;
  position: number;
  providerIdentifiers?: string[];
};

export type EntitlementItem = {
  id: string;
  masterId: string;
  product?: TEntitlementProduct;
  title?: EntitlementTitleType;
};

export type AtsToken = {
  aud: string;
  ctsTicket: string;
  exp: number;
  huaweiTicket: string;
  iss: string;
  nbf: number;
  partnerId: string;
  partnerName: string;
  subscriberCountry: string;
  subscriberId: string;
};

export type HuaweiLoginResponse = {
  encryptiontype: string;
  enctytoken: string;
  epghttpsurl: string;
  epgurl: string;
  platformcode: string;
  rootCerAddr: string;
  upgAddr4IPTV: string;
  upgAddr4OTT: string;
  upgHttpsAddr4IPTV: string;
  upgHttpsAddr4OTT: string;
  version: string;
};

export type AuthenticateErrorResponse = {
  data: null;
  message: string;
  messageCode: null;
  status: 'failure';
};

export type AuthenticateSuccessResponse = {
  data: {
    permissionLevel: string;
    sessionTicket: {
      identifier: string;
    };
    user: {
      crmId: string;
      crmSystem: string;
      customerCrmId: string;
      customerNumber: string;
      firstName: string;
      lastName: string;
      partnerNumber: number;
    };
  };
  message: string;
  messageCode: string;
  status: 'success';
};

export type AuthenticateResponse = AuthenticateErrorResponse | AuthenticateSuccessResponse;

export type AltiboxProfile = {
  address: string;
  authToken: string;
  profilePin: string;
  subscriberId: string;
};

export type AuthenticatorFromAltiboxResponse = {
  message: string;
  messageCode: null | string;
  status: string;
  value: AltiboxProfile[];
};

export type SwitchProfileResponse = {
  message: string;
  messageCode: null | string;
  status: string;
  value: AltiboxProfile[];
};

type KeyValue = { key: string; value?: string };

export type AltiboxAuthenticateResponse = {
  antiTamperURI: KeyValue[];
  areaid: string;
  bandwidth: string;
  bossID: string;
  ca: {
    playready: {
      LA_Url: string;
    };
    verimatrix: {
      company: string;
      csmport: string;
      multiRightsFairplay: {
        certificateServerUrl: string;
        laURL: string;
      };
      multiRightsPlayready: string;
      multiRightsWidevine: string;
      serverport: string;
    };
    widevine: {
      LA_Url: string;
    };
  };
  caDeviceInfo: {
    caDeviceId: string;
    caDeviceType: string;
    network: string;
    VUID: string;
  }[];
  configurations: {
    cfgType: string;
    extensionInfo: KeyValue[];
  }[];
  cSessionID: string;
  currenttime: string;
  deviceId: string;
  dsmdomain: string;
  dstTime: string;
  isFirstLogin: string;
  isInside: string;
  jSessionID: string;
  location: string;
  loginOccasion: string;
  mgmtdomain: string;
  needSignEULA: string;
  needSignPrivacyStatement: string;
  ntpdomain: string;
  ntpdomainbackup: string;
  opt: string;
  pageTrackers: [];
  parameters: {
    adplatformurl: string;
    adplayovernotifyurl: string;
    adpublicstrategyurl: string;
    bitband: string;
    bookmarklimit: string;
    DVBEanble: string;
    favoCatalogLimit: string;
    favouritelimit: string;
    giftLoyaltyByBrowseAd: string;
    giftLoyaltyByReceiveAdWithEmail: string;
    giftLoyaltyByReceiveAdWithSMS: string;
    issupportpublicad: string;
    locklimit: string;
    mashupaddress: string;
    pltvDelay: string;
    profilelimit: string;
    repeatTVLength: string;
    restartTVOffset: string;
    sqmurl: string;
    tvmsdelaylength: string;
    tvmsheartbitinterval: string;
    tvmsheartbiturl: string;
    tvmsvodheartbiturl: string;
  };
  pictureUrl: [];
  profiles: {
    channellistType: string;
    createTime: string;
    hidemessage: string;
    id: string;
    isDisplayInfoBar: string;
    isFilterLevel: string;
    lang: string;
    leadTimeForSendReminder: string;
    levels: string;
    loginName: string;
    multiscreenEnable: string;
    name: string;
    needSubscriberCnfmFlag: string;
    profilePINEnable: string;
    profiletype: string;
    purchaseEnable: string;
    quota: string;
    reminderInterval: string;
    reviceSMS: string;
    sendSMSForReminder: string;
    subscriberId: string;
  }[];
  provisioningType: string;
  pwdResetTime: string;
  registerNo: string;
  retcode: string;
  retmsg: string;
  returnCSessionIdInBody: string;
  returnJSessionIdInBody: string;
  rrsAddr: string;
  sessionid: string;
  subnetId: string;
  templatename: string;
  templateTimeStamp: string;
  timezone: string;
  transportprotocol: string;
  triggers: [];
  upgradedomain: string;
  usergroup: string;
  userID: string;
  users: {
    identityid: string;
    lang: string;
    role: string;
    username: string;
  }[];
  userToken: string;
};

export type Device = {
  caDeviceInfos: [
    {
      caDeviceId: string;
      caDeviceType: string;
      VUID: string;
    },
  ];
  definition: string;
  deviceId: string;
  deviceName: string;
  deviceToken: string;
  deviceType: string;
  ESTID: string;
  fps: string;
  isonline: string;
  isSupportPVR: string;
  isSupportRPVR: string;
  lastOfflineTime: string;
  physicalDeviceId: string;
  status: string;
  terminalType: string;
  terminalVendor: string;
  videoCodec: string;
};

export type GetDeviceListResponse = {
  deviceList: Device[];
  retcode: string;
};

export type GetReplaceTimesResponse = {
  remainTimes: string;
  retcode: string;
  retmsg: string;
  totalTimes: string;
};

export type Profile = {
  profileId: string;
  profileImage: string;
  profileName: string;
  subscriberId: string;
};
